import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Plant } from "src/model/class/general classes/plant";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { PlantConfigurationExternal } from "src/model/interface/plant-configuration-external";
import {
  currentPlantChangeConnectionAddressAction,
  currentPlantChangeSelectionAction,
  currentPlantChangeWanConnectionAction,
  currentPlantUnsetAction,
  plantAddAction,
  plantChangeWanAllowConnectionAction,
  plantRemoveAction,
  plantUpdateAction,
} from "src/state/actions/plant.actions";
import { listPlant, selectedPlant } from "src/state/selectors/plant.selectors";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoaderService } from "./loader.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: "root",
})
export class PlantService implements iEvent {
  private _logContext: string = "Plant.service";
  private _listPlantSubscription: Subscription = null;
  private _isDigitalPlaceSubscribtion: Subscription = null;
  private _listPlant: Array<Plant> = null;
  private _isDigitalPlace: boolean = false;
  private _idSelectedPlant: number = -1;
  private _currentPlant: Plant = null;

  get isDigitalPlacePlant() {
    return this._isDigitalPlace;
  }

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _eventService: EventService,
    private _localStorageService: LocalStorageService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _http: HttpClient,
    private _loaderService: LoaderService,
    //private _networkService: NetworkService,
    private _alertService: AlertService,
    private _themeService: ThemeService
  ) {
    this.subscribeEvents();
    this.loadListPlants();
    this.checkIsDigitalPlace();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeStoreSelect();
  }

  public getSelectedPlant() {
    return this._currentPlant;
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnSelectPlantShowLoginForm.subscribe(
        (selectedPlant: Plant) => {
          this._idSelectedPlant = selectedPlant?.ID || -1;

          this._store.dispatch(
            currentPlantChangeSelectionAction({
              selectedPlant: new Plant(selectedPlant),
            })
          );
        }
      )
    );
    this._eventSubscriptions.push(
      this._store.select(selectedPlant).subscribe((currentPlant: Plant) => {
        this._currentPlant = new Plant(currentPlant);
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnServerRootChanged.subscribe(
        (newConnectionAddress: string) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnServerRootChanged",
            "React to new ServerRoot: " + environment.httpServerRoot
          );
          if (environment.httpServerRoot !== "") {
            this._store.dispatch(
              currentPlantChangeConnectionAddressAction({
                newAddressConnection: newConnectionAddress,
              })
            );
          } else {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_eventService",
              "OnServerRootChanged",
              "No current http root"
            );
          }
        }
      )
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(() => {
        this.deselectCurrentPlant();
      })
    ); //

    this._eventSubscriptions.push(
      this._eventService.OnPlantReceivedWanBlockResponse.subscribe(
        (wanBlock: boolean) => {
          this.handleAllowWanConnectionFlag(!wanBlock);
        }
      )
    );

    /* When the login process has ended successfully, update the "current_plant" object in the local store */

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => { })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Get current plant server configuration
   *
   * @param fullServerEndpoint
   * @returns Current Plant configuration object
   */
  public async getCurrentPlantServerSync(
    fullServerEndpoint: string
  ): Promise<PlantConfigurationExternal> {
    return this._http
      .get<any>(
        fullServerEndpoint +
        AppSettings.API_URL_SYSTEM +
        "/network/configuration"
      )
      .pipe(
        map((plantConfiguration: any) =>
          this.castPlantConfigurationAPIObject(plantConfiguration)
        )
      )
      .toPromise();
  }

  /**
   * Check if plant exist
   *
   * @param plantToFind Plant to fined
   * @returns
   */
  public checkIfPlantExist(plantToFind: Plant): boolean {
    return (
      this._listPlant.find((arrayPlant) => {
        let arrayObjcetPlant = new Plant(arrayPlant);
        let plantToFindObject = new Plant(plantToFind);

        return (
          arrayObjcetPlant.FullLanAddress ===
          plantToFindObject.FullLanAddress &&
          arrayObjcetPlant.FullWanAddress === plantToFindObject.FullWanAddress
        );
      }) !== undefined
    );
  }

  /**
   * Convert plant configuration type (interface) to plant object
   *
   * @param plantConfiguration Plant configuration type
   * @returns Plant object
   */
  public convertPlantConfigurationToPlantObject(
    plantConfiguration: PlantConfigurationExternal
  ): Plant {
    const socketPortArray: Array<string> =
      plantConfiguration.SocketPort.split(",");
    const lanConfiugration: Array<string> =
      plantConfiguration.LANAddress.split(":");
    const wanConfiguration: Array<string> =
      plantConfiguration.WANAddress.split(":");

    const lanSocketPort: string = socketPortArray[0];
    const wanSocketPort: string = socketPortArray?.[1] || lanSocketPort;

    const plantComplete = new Plant();
    plantComplete.PlantName = plantConfiguration.PlantName;
    plantComplete.WANAddress = wanConfiguration[0];
    plantComplete.LANAddress = lanConfiugration[0];
    plantComplete.WanPort = wanConfiguration[1];
    plantComplete.LanPort = lanConfiugration[1];
    plantComplete.LANMqttPort = lanSocketPort;
    plantComplete.WANMqttPort = wanSocketPort;
    plantComplete.Username = plantConfiguration.Username;
    plantComplete.Password = plantConfiguration.Password;
    plantComplete.LANHttps =
      plantConfiguration.LANHttps?.toString().toUpperCase() === "TRUE";
    plantComplete.WANHttps =
      plantConfiguration.WANHttps?.toString().toUpperCase() === "TRUE";

    return plantComplete;
  }

  /**
   * Insert newly created plant in public observable list AND local storage.
   *
   * ALSO, it checks if app has been compiled with theme "DigitalPlace"; if so, it sets the Operative Mode
   * of the newly inserted plant to "DIGITAL_PLACE_MODE".
   *
   * @param newPlant New plant to be inserted in store
   * @param deselectCurrentPlant Flag; if the newly created plant should be also selected as "current plant", it is set to FALSE.
   * Otherwise, it is set to TRUE. DEFAULT: FALSE
   * @returns
   */
  public insertPlant(
    newPlant: Plant,
    deselectCurrentPlant: boolean = false
  ): Plant {
    newPlant.ID = this.calculatePlantID();

    if (this._isDigitalPlace) {
      //newPlant.operativeMode = BIGDeviceOperationMode.DIGITAL_PLACE_MODE;
      newPlant = this.modifyPlantForDigitalPlace(newPlant);
    }

    this._store.dispatch(plantAddAction({ newPlant }));

    if (deselectCurrentPlant) {
      this.deselectCurrentPlant();
    } else {
      this._store.dispatch(
        currentPlantChangeSelectionAction({ selectedPlant: newPlant })
      );

      this._eventService.OnSelectPlantShowLoginForm.next(newPlant);
    }

    return newPlant;
  }

  /**
   * Update plant in public observable list AND local storage.
   *
   * @param updatePlant Plant to be updated
   * @param deselectCurrentPlant Flag; if the updated plant should be also selected as "current plant", it is set to FALSE.
   * Otherwise, it is set to TRUE. DEFAULT: FALSE
   */
  public updatePlant(
    updatePlant: Plant,
    deselectCurrentPlant: boolean = false
  ): void {
    this._store.dispatch(plantUpdateAction({ updatePlant }));

    if (deselectCurrentPlant) {
      this.deselectCurrentPlant();
    } else {
      this._store.dispatch(
        currentPlantChangeSelectionAction({ selectedPlant: updatePlant })
      );
    }
  }

  /**
   * Remove single plant by id
   *
   * @param idPlant ID Plant to remote
   */
  public removePlant(idPlant: number): void {
    this._store.dispatch(plantRemoveAction({ idPlant }));

    this.deselectCurrentPlant();
    this.removeCustomAggregationKeyFromStorage(idPlant);
  }

  /**
   * Deselect currently selected plant; use this when no plant should be considered
   */
  public deselectCurrentPlant(): void {
    this._idSelectedPlant = -1;

    this._store.dispatch(currentPlantUnsetAction());
  }

  /**
   *Calculate plant id by existing plant list
   *
   * @returns
   */
  private calculatePlantID(): number {
    let idPlant = Math.max.apply(
      Math,
      this._listPlant.map(function (o) {
        return o.ID;
      })
    );

    if (idPlant === -Infinity) {
      idPlant = 1;
    } else {
      idPlant++;
    }

    return idPlant;
  }

  /**
   * Remove plant aggregator custom interface component from storage
   *
   * @param idPlant id Plant to remove releated key (if exist)
   */
  private removeCustomAggregationKeyFromStorage(idPlant: number): void {
    this._localStorageService.remove(
      AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + idPlant
    );
  }

  /**
   * Handle wan connection case.
   *
   * @param allowWanConnection Allow wan connection flag
   */
  private handleAllowWanConnectionFlag(allowWanConnection: boolean): void {

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleAllowWanConnectionFlag",
      "Set 'allowWanConnection' to: " + allowWanConnection + " | for plant: " + this._idSelectedPlant
    );

    this._store.dispatch(
      plantChangeWanAllowConnectionAction({
        allowWanConnection,
        idPlant: this._idSelectedPlant,
      })
    );

    this._store.dispatch(
      currentPlantChangeWanConnectionAction({ allowWanConnection })
    );
  }

  /**
   * Load list plants
   */
  private loadListPlants(): void {
    this._listPlantSubscription = this._store
      .select(listPlant)
      .pipe(filter((listPlant: Array<Plant>) => listPlant !== null))
      .subscribe((listPlant: Array<Plant>) => {
        this._listPlant = listPlant;
      });
  }

  /**
   * Check from "clientSetting" feature if app has currently "digitalPlace" theme.
   */
  private checkIsDigitalPlace() {
    this._isDigitalPlace = this._themeService.isDigitalPlace;
  }

  /**
   * Cast generic config API object to specific PlantConfigurationExternal
   *
   * @param plantAPI Plant configuration
   *
   * @returns Plant configuration external object
   */
  private castPlantConfigurationAPIObject(
    plantAPI: any
  ): PlantConfigurationExternal {
    return {
      PlantName: plantAPI["plantName"],
      WANAddress: plantAPI["wanAddress"],
      LANAddress: plantAPI["lanAddress"],
      Username: plantAPI["username"],
      Password: plantAPI["password"],
      SocketPort: plantAPI["socketPort"],
      CardNumber: plantAPI["cardNumber"],
      LANHttps: plantAPI?.["lanHttps"] || false,
      WANHttps: plantAPI?.["wanHttps"] || false,
    };
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._listPlantSubscription?.unsubscribe();
    this._isDigitalPlaceSubscribtion?.unsubscribe();
  }
  //#endregion

  // public async performPlantDirectSync(httpsEnable: boolean, ipAddress: string, APIport: string): Promise<boolean> {
  /**
   * Handle plant direct syncronization.
     If sync end with success, it select inserted plant AS "currentPlant", then return TRUE.
     Otherwise, it return FALSE.
   * @param fullServerEndpoint String containing the the FULL endpoint to contact for direct synchronization function. This string MUST
     follow the following pattern: HTTP/HTTPS +"://" +" IP address + ":" + API PORT. 

     It is advised to use method "_networkService.getGenericURIByIPandPort(...)" in order to generate it.
   * @returns 
   */
  public async performPlantDirectSync(
    fullServerEndpoint: string
  ): Promise<boolean> {
    let result: boolean = false;

    try {
      await this._loaderService.presentLoaderWaiting();

      const plantConfiguration: PlantConfigurationExternal =
        await this.getCurrentPlantServerSync(fullServerEndpoint);

      if (plantConfiguration.PlantName === "") {
        await this._loaderService.dismissLoaderWaiting();

        plantConfiguration.PlantName =
          await this._alertService.showAlertPlantName(plantConfiguration);

        await this._loaderService.presentLoaderWaiting();
      }
      await this.performInsertPlantFromExternalData(plantConfiguration);

      //this._systemRouterService.navigateToLoginPlant();

      result = true;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "performPlantDirectSync()",
        "ERROR: " + (exception?.message ?? JSON.stringify(exception))
      );
      if ((exception as HttpErrorResponse)?.status === 404) {
        await this._loaderService.dismissLoaderWaiting();
        await this._alertService.showAlertUnsupportedFeature();
      } else {
        await this._loaderService.dismissLoaderWaiting();
        await this._alertService.showAlertErrorServerSync();
      }
      result = false;
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      return result;
    }
  }

  /**
   * When previsous API call return info for palnt-syncronization, this method checks if plant exist in list in local store,
   * if not, it proceeds to insert it.
   *
   * @param plantConfiguration Plant Configuration taken from API call.
   */
  private async performInsertPlantFromExternalData(
    plantConfiguration: PlantConfigurationExternal
  ): Promise<void> {
    // try {
    const plantObject: Plant =
      this.convertPlantConfigurationToPlantObject(plantConfiguration);

    this.insertPlant(plantObject);
    /*
  } catch (exception) {
    await this._alertService.showAlertErrorServerSync();

  }
  */
  }

  //NOTE: SI DEVE POI PENSARE QUANDO SARà PRONTO IL MECCANISMO DI INSERIMENTO/SINCRONIZZAZIONE IMPIANTI IN DIGITAL_PLACE SE
  //MANTENERLO COSì O INVERTIRE LA STRUTTURA (IE. INVOCARE QUESTO METODO PUBBLICAMENTE, PER POI AL SUO INTERNO INVOCARE insertPlant(...) PER
  //GESTIRE L'INSERIMENTO FISICO DELL'IMPIANTO.
  /**
   * This method handles the correct rapresentation of a "Digital Place"-specific plant inside the App, making sure all the
   * properties of the associted "Plant.ts" object are properly valorized.
   * @param plantToInsert The
   * @returns The resulting, fully-converted plant object
   */
  private modifyPlantForDigitalPlace(plantToInsert: Plant): Plant {
    plantToInsert.operativeMode = BIGDeviceOperationMode.DIGITAL_PLACE_MODE;

    //OTHER ACTIONS

    return plantToInsert;
  }
}
